// "See available straps" on the watch product page — Tissot-style pill below
// the colourway swatches that opens a modal listing the strap/bracelet
// products that fit this watch. A strap's custom.title_internal metafield
// holds the semicolon-separated list of watch groups it fits; the watch's
// custom.straps_options metafield holds one or more of those group tokens
// (also semicolon-separated, Matrixify-style). Accessory records come from
// Algolia by the product_type facet and are matched token-against-token
// client-side. The `.js-straps-options` mount in layouts/product.edge is
// only rendered when the metafield is set, and stays hidden unless at least
// one matching strap is in stock.
import algoliasearch from 'algoliasearch/lite';
import { stardust } from '@eidellev/adonis-stardust/client';
import { getIndexName } from '../../../../app/Libraries/Algolia/AlgoliaUtils';
import { getSizedShopifyImageUrl, getPageLang, formatAsMoney } from '../utils';
import { getInternalTitle, isInStock } from '../title-swatch-utils';

const MODAL_NAME = 'straps-options';

const escapeHtml = (value: unknown) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char] as string));

/** The strap pill + modal are rebuilt per product — drop the previous pair */
const removeStaleModal = () => {
  document.querySelector(`.js-modal[data-modal="${MODAL_NAME}"]`)?.remove();
};

/**
 * Key features aren't in the Algolia records, so they're scraped from the
 * strap's own (server-rendered) product page, cached per handle
 */
const specsCache = new Map<string, { key: string; value: string }[]>();
const fetchSpecs = async (url: string, handle: string) => {
  if (specsCache.has(handle)) return specsCache.get(handle)!;
  const response = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const specs = Array.from(doc.querySelectorAll('.single-product__key-feature p'))
    .map((p) => {
      const key = p.querySelector('strong')?.textContent?.trim() || '';
      const value = (p.textContent || '').replace(key, '').trim();
      return { key, value };
    })
    .filter((spec) => spec.key && spec.value);
  specsCache.set(handle, specs);
  return specs;
};

const init = async () => {
  const mount = document.querySelector<HTMLElement>('.js-straps-options:not([data-straps-bound])');
  if (!mount) return;
  mount.setAttribute('data-straps-bound', 'true');

  // This watch's fitment tokens, semicolon-separated
  const names = String(mount.dataset.strapsOptions || '')
    .split(';')
    .map((name) => name.trim())
    .filter(Boolean);
  if (!names.length) return;
  const nameKeys = names.map((name) => name.toLowerCase());

  // Position of a strap in the curated order: the lowest index among the
  // watch tokens that appear in the strap's own fitment list (Infinity = no
  // overlap, i.e. the strap doesn't fit this watch)
  const fitIndex = (hit: any): number => {
    const indexes = getInternalTitle(hit)
      .split(';')
      .map((token) => nameKeys.indexOf(token.trim().toLowerCase()))
      .filter((index) => index !== -1);
    return indexes.length ? Math.min(...indexes) : Infinity;
  };

  try {
    const searchClient = algoliasearch(
      process.env.ALGOLIA_APP_ID as string,
      process.env.ALGOLIA_SEARCH_KEY as string,
    );
    // Every accessory record — a strap's fitment list can't be facet-matched
    // exactly (it's a semicolon list), so matching happens client-side
    const { results } = await searchClient.search([{
      indexName: getIndexName(getPageLang(), 'featured', 'asc'),
      query: '',
      params: {
        hitsPerPage: 500,
        attributesToRetrieve: [
          'title', 'handle', 'image', 'price', 'meta', 'product_type', 'inventory_available',
          'named_tags', 'option_names', 'body_html_safe',
        ],
        analytics: false,
        clickAnalytics: false,
        facetFilters: [['product_type:Timepieces Accessories']],
      },
    }] as any);

    // Records are per-variant — keep one in-stock fitting strap per product,
    // ordered by the watch's token order
    const straps = (((results[0] as any).hits) || [])
      .filter((hit: any) =>
        isInStock(hit)
        && fitIndex(hit) !== Infinity
        && !!hit.handle)
      .filter((hit: any, index: number, hits: any[]) =>
        hits.findIndex((other) => other.handle === hit.handle) === index)
      .sort((a: any, b: any) => fitIndex(a) - fitIndex(b));
    if (!straps.length) return;

    const labelTemplate = straps.length === 1
      ? mount.dataset.labelOne || ''
      : mount.dataset.labelMany || '';
    // %count% (not ICU {count}) — the server-side t() would demand a value
    const label = labelTemplate.replace('%count%', String(straps.length));
    const thumb = straps[0].image
      ? getSizedShopifyImageUrl(straps[0].image, 200)
      : '/img/placeholder.png';

    mount.innerHTML = `
      <button type="button" class="straps-options__trigger js-straps-trigger">
        <img class="straps-options__thumb" src="${escapeHtml(thumb)}" alt="" loading="lazy" />
        <span class="straps-options__label">${escapeHtml(label)}</span>
        <span class="straps-options__chevron" aria-hidden="true"></span>
      </button>`;
    mount.hidden = false;

    // Add-to-cart goes through the app bundle's delegated .js-add-to-cart
    // handler, which is bound to the You-May-Also-Like .product-list container
    // — the modal must live inside it to reach the shared cart singleton
    const cartContainer = document.querySelector('.product-list');

    const strapUrl = (strap: any) =>
      stardust.route(`${getPageLang()}.shopify.slug`, { slug: strap.handle });
    const strapPrice = (strap: any) => {
      const priceNumber = Number(strap.price);
      return Number.isFinite(priceNumber) && priceNumber > 0
        ? formatAsMoney(priceNumber, getPageLang())
        : '';
    };
    // Same rules as the collection grid: online + in stock + no size/etc.
    // selection needed
    const isPurchasable = (strap: any) => !!cartContainer
      && !!strap.named_tags?.available
      && !!strap.inventory_available
      && !((strap.option_names || []).length > 1);

    const items = straps
      .map((strap: any, index: number) => {
        const url = strapUrl(strap);
        const image = strap.image
          ? getSizedShopifyImageUrl(strap.image, 400)
          : '/img/placeholder.png';
        const price = strapPrice(strap);
        const bagButton = isPurchasable(strap)
          ? `<button type="button" class="bag-btn straps-modal__bag js-add-to-cart"
              data-id="gid://shopify/ProductVariant/${escapeHtml(strap.objectID)}">
              <span class="sr-only">${escapeHtml(mount.dataset.cartLabel || 'Add to Cart')}</span>
            </button>`
          : '';

        return `
          <li class="straps-modal__cell" data-strap-index="${index}">
            <div class="straps-modal__media">
              <a class="straps-modal__thumb js-straps-detail-link" href="${escapeHtml(url)}"
                title="${escapeHtml(strap.title)}" aria-label="${escapeHtml(strap.title)}">
                <img src="${escapeHtml(image)}" alt="" loading="lazy" />
              </a>
              ${bagButton}
            </div>
            <a class="straps-modal__caption js-straps-detail-link" href="${escapeHtml(url)}" tabindex="-1">
              <span class="straps-modal__name">${escapeHtml(strap.title)}</span>
              ${price ? `<span class="straps-modal__price">${escapeHtml(price)}</span>` : ''}
            </a>
          </li>`;
      })
      .join('');

    removeStaleModal();
    const modal = document.createElement('div');
    modal.className = 'js-modal modal straps-modal';
    modal.setAttribute('data-modal', MODAL_NAME);
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="modal__content straps-modal__content">
        <button type="button" class="js-modal-close straps-modal__close"
          aria-label="${escapeHtml(mount.dataset.closeLabel || 'Close')}"></button>
        <div class="straps-modal__grid-view">
          <h2 class="straps-modal__title">${escapeHtml(mount.dataset.modalTitle || '')}</h2>
          <ul class="straps-modal__grid">${items}</ul>
        </div>
        <div class="straps-modal__detail js-straps-detail" hidden></div>
      </div>`;
    (cartContainer || document.body).appendChild(modal);

    const gridView = modal.querySelector<HTMLElement>('.straps-modal__grid-view')!;
    const detailView = modal.querySelector<HTMLElement>('.js-straps-detail')!;
    let successObserver: MutationObserver | null = null;
    // Bumped on every view change so stale async work (spec fetches) is ignored
    let viewToken = 0;

    const showGrid = () => {
      viewToken += 1;
      successObserver?.disconnect();
      successObserver = null;
      detailView.hidden = true;
      detailView.innerHTML = '';
      gridView.hidden = false;
    };

    // Tissot-style "Product added to cart" view with a See-cart CTA — shown
    // when the shared cart handler marks our detail CTA with .success
    const showConfirm = (strap: any) => {
      viewToken += 1;
      successObserver?.disconnect();
      successObserver = null;
      const image = strap.image
        ? getSizedShopifyImageUrl(strap.image, 200)
        : '/img/placeholder.png';
      const price = strapPrice(strap);

      detailView.innerHTML = `
        <button type="button" class="straps-modal__back js-straps-back">
          ${escapeHtml(mount.dataset.backLabel || 'Back')}
        </button>
        <p class="straps-modal__confirm-head">
          <span class="straps-modal__confirm-check" aria-hidden="true"></span>
          ${escapeHtml(mount.dataset.addedLabel || 'Product added to cart')}
        </p>
        <div class="straps-modal__confirm-item">
          <div class="straps-modal__confirm-thumb"><img src="${escapeHtml(image)}" alt="" /></div>
          <div class="straps-modal__confirm-info">
            <span class="straps-modal__detail-name">${escapeHtml(strap.title)}</span>
            ${price ? `<span class="straps-modal__detail-price">${escapeHtml(price)}</span>` : ''}
          </div>
        </div>
        <div class="straps-modal__detail-cta">
          <a class="btn btn--solid-navy straps-modal__cta" href="${escapeHtml(mount.dataset.cartUrl || '/shopping-bag')}">
            ${escapeHtml(mount.dataset.seeCartLabel || 'See cart')}
          </a>
        </div>`;
      gridView.hidden = true;
      detailView.hidden = false;
      detailView.scrollTop = 0;
    };

    // Tissot-style in-drawer product view: BACK, big image, name, price,
    // short description, link to the full page, and a solid add-to-cart CTA
    const showDetail = (strap: any) => {
      const url = strapUrl(strap);
      const image = strap.image
        ? getSizedShopifyImageUrl(strap.image, 800)
        : '/img/placeholder.png';
      const price = strapPrice(strap);
      const description = String(strap.body_html_safe || '').trim();
      const cta = isPurchasable(strap)
        ? `<button type="button" class="btn btn--solid-navy straps-modal__cta js-add-to-cart"
            data-id="gid://shopify/ProductVariant/${escapeHtml(strap.objectID)}">
            ${escapeHtml(mount.dataset.cartLabel || 'Add to Cart')}
          </button>`
        : `<a class="btn btn--solid-navy straps-modal__cta" href="${escapeHtml(url)}">
            ${escapeHtml(mount.dataset.detailsLabel || 'View details')}
          </a>`;

      detailView.innerHTML = `
        <button type="button" class="straps-modal__back js-straps-back">
          ${escapeHtml(mount.dataset.backLabel || 'Back')}
        </button>
        <div class="straps-modal__detail-card">
          <img src="${escapeHtml(image)}" alt="${escapeHtml(strap.title)}" />
        </div>
        <h3 class="straps-modal__detail-name">${escapeHtml(strap.title)}</h3>
        ${price ? `<p class="straps-modal__detail-price">${escapeHtml(price)}</p>` : ''}
        <div class="straps-modal__detail-cta straps-modal__detail-cta--inline">${cta}</div>
        ${description ? `<p class="straps-modal__detail-desc">${escapeHtml(description)}</p>` : ''}
        <div class="straps-modal__specs js-straps-specs"></div>`;
      gridView.hidden = true;
      detailView.hidden = false;
      detailView.scrollTop = 0;
      viewToken += 1;
      detailView.querySelector<HTMLElement>('.js-straps-back')?.focus();

      // Key features arrive async from the strap's own product page
      const token = viewToken;
      fetchSpecs(url, strap.handle)
        .then((specs) => {
          const specsMount = detailView.querySelector('.js-straps-specs');
          if (token !== viewToken || !specsMount || !specs.length) return;
          specsMount.innerHTML = `
            <h4 class="straps-modal__specs-title">${escapeHtml(mount.dataset.featuresLabel || 'Key Features:')}</h4>
            <ul class="straps-modal__specs-list">
              ${specs.map((spec) => `
                <li><strong>${escapeHtml(spec.key)}</strong> ${escapeHtml(spec.value)}</li>
              `).join('')}
            </ul>`;
        })
        .catch(() => {
          // Specs unavailable — the section just stays empty
        });

      // Flip to the confirmation view once the shared cart handler marks the
      // CTA as successfully added
      const ctaButton = detailView.querySelector<HTMLElement>('.js-add-to-cart');
      successObserver?.disconnect();
      successObserver = null;
      if (ctaButton) {
        successObserver = new MutationObserver(() => {
          if (ctaButton.classList.contains('success')) {
            showConfirm(strap);
          }
        });
        successObserver.observe(ctaButton, { attributes: true, attributeFilter: ['class'] });
      }
    };

    // modal.ts binds triggers only on DOMContentLoaded/product:swap, which
    // have already fired by the time this async render lands — bind directly
    // via the window-exposed helpers (Escape is handled globally)
    mount.querySelector('.js-straps-trigger')?.addEventListener('click', () => {
      showGrid(); // always reopen on the grid
      window.openModal(MODAL_NAME);
    });
    modal.querySelector('.js-modal-close')?.addEventListener('click', () => {
      window.closeModal(MODAL_NAME);
    });
    modal.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const detailLink = target.closest<HTMLElement>('.js-straps-detail-link');
      if (detailLink) {
        e.preventDefault();
        const index = Number(detailLink.closest<HTMLElement>('[data-strap-index]')?.dataset.strapIndex);
        if (straps[index]) showDetail(straps[index]);
        return;
      }
      if (target.closest('.js-straps-back')) {
        showGrid();
        return;
      }
      if (!target.closest('.modal__content')) {
        window.closeModal(MODAL_NAME);
      }
    });
  } catch {
    // Algolia unavailable — the pill just stays hidden
  }
};

document.addEventListener('DOMContentLoaded', init);

// The colourway swap transplants a fresh (unbound) mount into the details
// column — rebuild for the new product, or clear the modal if it has none
document.addEventListener('product:swap', () => {
  if (!document.querySelector('.js-straps-options:not([data-straps-bound])')) {
    removeStaleModal();
  }
  init();
});
